import React, {
  FC,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DropChildrenCont, FlexBodyCont, FlexibleDropCont } from './FlexibleDropdown.styles';
import FlexibleDropdownTile from './FlexibleDropdownTile/FlexibleDropDownTile';

export interface IDropdownItem {
  id: string;
  name: string;
  icon?: string;
  [key: string]: unknown;
}

interface IFlexibleActivePos {
  key: string;
  value: string;
}

interface IFlexibleDropdown {
  children: ReactElement;
  items: IDropdownItem[];
  activeTile: IFlexibleActivePos;
  getSelectedItem: (item: IDropdownItem) => void;
  setActiveIcon?: (state: boolean) => void;
  width?: string;
  anchor?: 'top' | 'bottom';
  positionX?: string;
  positionY?: string;
}

const FlexibleDropdown: FC<IFlexibleDropdown> = ({
  children,
  setActiveIcon,
  items,
  getSelectedItem,
  width,
  anchor = 'bottom',
  positionX,
  positionY,
  activeTile,
}) => {
  const [active, setActive] = useState(false);
  const dropRef = useRef<HTMLDivElement | null>(null);

  const detectClickOutside = ({ target }: MouseEvent) => {
    const { current } = dropRef;

    if (current && !current.contains(target as Node)) {
      if (setActiveIcon) {
        setActiveIcon(false);
      }
      setActive(false);
    }
  };

  const makeSelection = (selectedItem: IDropdownItem) => {
    getSelectedItem(selectedItem);
    return setActive(false);
  };

  const manageSetActive = (status: boolean) => {
    if (setActiveIcon) {
      setActiveIcon(status);
    }
    setActive(status);
  };

  const renderBodyList = useMemo(() => {
    return items?.map((item) => {
      const { key, value } = activeTile;
      const isActive = item[key] === value;

      return (
        <FlexibleDropdownTile active={isActive} key={item.id} item={item} setItem={makeSelection} />
      );
    });
  }, [activeTile, items]);

  useEffect(() => {
    document.addEventListener('click', detectClickOutside);

    return () => {
      document.removeEventListener('click', detectClickOutside);
    };
  }, []);

  return (
    <FlexibleDropCont id="FlexibleDropCont" ref={dropRef}>
      <DropChildrenCont data-testid="OpenLangModal" onClick={() => manageSetActive(!active)}>
        {children}
      </DropChildrenCont>
      {active && (
        <FlexBodyCont
          data-testid="FlexBodyCont"
          width={width}
          anchor={anchor}
          positionX={positionX}
          positionY={positionY}
        >
          {renderBodyList}
        </FlexBodyCont>
      )}
    </FlexibleDropCont>
  );
};

export default FlexibleDropdown;
